import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, Tooltip, Paper, ToggleButtonGroup, ToggleButton } from '@mui/material';

type ViewMode = 'effectSize' | 'consistency';

export interface QuadrantDataPoint {
  id: string;
  label: string;
  accuracy: number;       // 0-1 (X-axis in both views)
  effectSize: number;     // Y-axis in effectSize view, color in consistency view
  consistency: number | null; // Y-axis in consistency view
  sampleSize: number;     // Bubble size in consistency view, fixed in effectSize view
  color?: string;         // Override color (effectSize view)
}

interface QuadrantChartProps {
  data: QuadrantDataPoint[];
  width?: number;
  height?: number;
}

function getEffectSizeColor(effectSize: number): string {
  if (effectSize > 10) return '#2e7d32';   // Dark green
  if (effectSize > 5) return '#66bb6a';    // Green
  if (effectSize > 0) return '#a5d6a7';    // Light green
  if (effectSize > -5) return '#ef9a9a';   // Light red
  if (effectSize > -10) return '#e57373';  // Red
  return '#c62828';                         // Dark red
}

const VIEW_CONFIG = {
  effectSize: {
    yLabel: 'Effect Size (%)',
    yMax: 20,
    yThreshold: 5,
    quadrants: {
      topRight: { name: 'STARS', desc: 'High Acc, Large Effect' },
      topLeft: { name: 'SNIPERS', desc: 'Low Acc, Large Effect' },
      bottomRight: { name: 'SCALPERS', desc: 'High Acc, Small Effect' },
      bottomLeft: { name: 'GAMBLERS', desc: 'Low Acc, Small Effect' },
    },
    getY: (p: QuadrantDataPoint) => Math.abs(p.effectSize),
    getRadius: () => 6,
    getColor: (p: QuadrantDataPoint, fallback: string) => p.color || fallback,
    formatTooltipY: (p: QuadrantDataPoint) => `Effect: ${p.effectSize > 0 ? '+' : ''}${p.effectSize.toFixed(1)}%`,
  },
  consistency: {
    yLabel: 'Consistency',
    yMax: 1,
    yThreshold: 0.5,
    quadrants: {
      topRight: { name: 'RELIABLE', desc: 'High Acc, Stable' },
      topLeft: { name: 'PREDICTABLY WRONG', desc: 'Low Acc, Stable' },
      bottomRight: { name: 'HOT STREAK', desc: 'High Acc, Volatile' },
      bottomLeft: { name: 'COIN FLIP', desc: 'Low Acc, Volatile' },
    },
    getY: (p: QuadrantDataPoint) => p.consistency ?? 0,
    getRadius: (p: QuadrantDataPoint) => Math.max(4, Math.min(16, Math.sqrt(p.sampleSize) * 1.5)),
    getColor: (p: QuadrantDataPoint) => getEffectSizeColor(p.effectSize),
    formatTooltipY: (p: QuadrantDataPoint) => `Consistency: ${p.consistency !== null ? `${(p.consistency * 100).toFixed(0)}%` : 'N/A'}`,
  },
} as const;

export const QuadrantChart: React.FC<QuadrantChartProps> = ({
  data,
  width = 400,
  height = 400
}) => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>('effectSize');
  const config = VIEW_CONFIG[viewMode];
  const padding = 40;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  const xScale = (val: number) => val * chartWidth;
  const yScale = (val: number) => {
    const clamped = Math.min(Math.max(val, 0), config.yMax);
    return chartHeight - (clamped / config.yMax) * chartHeight;
  };

  const xThreshold = 0.5;

  // Filter out points with null consistency in consistency view
  const visibleData = viewMode === 'consistency'
    ? data.filter(p => p.consistency !== null)
    : data;

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Signal Classification
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
        >
          <ToggleButton value="effectSize" sx={{ fontSize: '0.7rem', px: 1.5 }}>
            Effect Size
          </ToggleButton>
          <ToggleButton value="consistency" sx={{ fontSize: '0.7rem', px: 1.5 }}>
            Consistency
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === 'consistency' && visibleData.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="text.secondary">
            Waiting for consistency data (need ≥3 weeks with ≥3 signals each, ≥20 total)
          </Typography>
        </Box>
      )}

      {(viewMode === 'effectSize' || visibleData.length > 0) && (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          <g transform={`translate(${padding}, ${padding})`}>
            <rect x={0} y={0} width={chartWidth} height={chartHeight} fill={theme.palette.background.default} />

            {/* Threshold Lines */}
            <line
              x1={xScale(xThreshold)} y1={0}
              x2={xScale(xThreshold)} y2={chartHeight}
              stroke={theme.palette.divider} strokeDasharray="4"
            />
            <line
              x1={0} y1={yScale(config.yThreshold)}
              x2={chartWidth} y2={yScale(config.yThreshold)}
              stroke={theme.palette.divider} strokeDasharray="4"
            />

            {/* Quadrant Labels */}
            {(['topRight', 'topLeft', 'bottomRight', 'bottomLeft'] as const).map(pos => {
              const q = config.quadrants[pos];
              const cx = pos.includes('Right') ? chartWidth * 0.75 : chartWidth * 0.25;
              const cy = pos.includes('top') ? chartHeight * 0.15 : chartHeight * 0.85;
              return (
                <g key={pos}>
                  <text x={cx} y={cy} textAnchor="middle" fill={theme.palette.text.disabled} fontSize="10" fontWeight="600">
                    {q.name}
                  </text>
                  <text x={cx} y={cy + 13} textAnchor="middle" fill={theme.palette.text.disabled} fontSize="8">
                    {q.desc}
                  </text>
                </g>
              );
            })}

            {/* Axes */}
            <line x1={0} y1={chartHeight} x2={chartWidth} y2={chartHeight} stroke={theme.palette.text.primary} />
            <line x1={0} y1={0} x2={0} y2={chartHeight} stroke={theme.palette.text.primary} />

            {/* Axis Labels */}
            <text x={chartWidth / 2} y={chartHeight + 30} textAnchor="middle" fontSize="11" fill={theme.palette.text.secondary}>
              Accuracy
            </text>
            <text x={-chartHeight / 2} y={-28} transform="rotate(-90)" textAnchor="middle" fontSize="11" fill={theme.palette.text.secondary}>
              {config.yLabel}
            </text>

            {/* X Ticks */}
            {[0, 0.25, 0.5, 0.75, 1].map(tick => (
              <g key={`xtick-${tick}`}>
                <line x1={xScale(tick)} y1={chartHeight} x2={xScale(tick)} y2={chartHeight + 5} stroke="currentColor" />
                <text x={xScale(tick)} y={chartHeight + 15} textAnchor="middle" fontSize="9" fill={theme.palette.text.secondary}>
                  {tick * 100}%
                </text>
              </g>
            ))}

            {/* Y Ticks */}
            {viewMode === 'effectSize'
              ? [0, 5, 10, 15, 20].map(tick => (
                <g key={`ytick-${tick}`}>
                  <line x1={-5} y1={yScale(tick)} x2={0} y2={yScale(tick)} stroke="currentColor" />
                  <text x={-8} y={yScale(tick) + 3} textAnchor="end" fontSize="9" fill={theme.palette.text.secondary}>
                    {tick}%
                  </text>
                </g>
              ))
              : [0, 0.25, 0.5, 0.75, 1].map(tick => (
                <g key={`ytick-${tick}`}>
                  <line x1={-5} y1={yScale(tick)} x2={0} y2={yScale(tick)} stroke="currentColor" />
                  <text x={-8} y={yScale(tick) + 3} textAnchor="end" fontSize="9" fill={theme.palette.text.secondary}>
                    {(tick * 100).toFixed(0)}%
                  </text>
                </g>
              ))
            }

            {/* Data Points */}
            {visibleData.map((point) => {
              const cx = xScale(point.accuracy);
              const cy = yScale(config.getY(point));
              const r = config.getRadius(point);
              const fill = config.getColor(point, theme.palette.primary.main);
              const tooltipText = `${point.label}: Acc ${(point.accuracy * 100).toFixed(1)}%, ${config.formatTooltipY(point)}, n=${point.sampleSize}`;
              return (
                <g key={point.id}>
                  <Tooltip title={tooltipText}>
                    <circle
                      cx={cx}
                      cy={cy}
                      r={r}
                      fill={fill}
                      stroke="#fff"
                      strokeWidth={1.5}
                      style={{ cursor: 'pointer' }}
                    />
                  </Tooltip>
                </g>
              );
            })}
          </g>
        </svg>
      )}

      {viewMode === 'consistency' && visibleData.length > 0 && (
        <Box display="flex" justifyContent="center" gap={1} mt={1} flexWrap="wrap">
          {[
            { label: '>10%', color: '#2e7d32' },
            { label: '5-10%', color: '#66bb6a' },
            { label: '0-5%', color: '#a5d6a7' },
            { label: '-5-0%', color: '#ef9a9a' },
            { label: '<-5%', color: '#e57373' },
          ].map(item => (
            <Box key={item.label} display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
            </Box>
          ))}
          <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
            (effect size)
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
